import { useEffect } from "react";

/**
 * SeoHead — lightweight, per-route document head management for a CRA SPA.
 * Updates title, description, canonical, OpenGraph, Twitter, and (optionally)
 * JSON-LD at runtime. Cleans up nothing on unmount because each route calls it
 * with its own set (the next route overwrites the same tags).
 */

function setMeta(selector, attr, value) {
  if (value == null) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [_, key, name] = selector.match(/meta\[(.+?)="(.+?)"\]/) || [];
    if (key && name) el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, obj) {
  if (!obj) return;
  let el = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

export default function SeoHead({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType,
  twitterTitle,
  twitterDescription,
  twitterImage,
  twitterCard,
  jsonLd,
  jsonLdId = "page-jsonld",
}) {
  useEffect(() => {
    if (title) document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setCanonical(canonical);

    setMeta('meta[property="og:title"]', "content", ogTitle || title);
    setMeta('meta[property="og:description"]', "content", ogDescription || description);
    if (ogImage) setMeta('meta[property="og:image"]', "content", ogImage);
    if (ogUrl) setMeta('meta[property="og:url"]', "content", ogUrl);
    if (ogType) setMeta('meta[property="og:type"]', "content", ogType);

    setMeta('meta[name="twitter:card"]', "content", twitterCard || "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", twitterTitle || ogTitle || title);
    setMeta('meta[name="twitter:description"]', "content", twitterDescription || ogDescription || description);
    if (twitterImage) setMeta('meta[name="twitter:image"]', "content", twitterImage);

    if (jsonLd) setJsonLd(jsonLdId, jsonLd);
  }, [
    title, description, canonical,
    ogTitle, ogDescription, ogImage, ogUrl, ogType,
    twitterTitle, twitterDescription, twitterImage, twitterCard,
    jsonLd, jsonLdId,
  ]);

  return null;
}
